import { Component, OnInit, inject } from '@angular/core';

import { UserSettingsService } from 'services/user-settings.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TimeFormat, UserSettings } from 'models/user-settings';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { providerConfig } from '../../../providerconfig';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { ColorScheme } from 'services/theme.service';
import { MatButton } from '@angular/material/button';
import { AccountService } from 'services/account.service';
import { SnackBarService } from 'services/snackbar.service';
import { of } from 'rxjs';
import { CMSValidators } from 'shared/utils/validators';
import { AppConfigService } from 'services/app-config.service';
import { AppConfig } from 'models/app-config';

interface LegalLink {
  labelTranslationKey: string;
  url: string;
}

type LegalUrlKey = 'gdpr' | 'termsAndConditions' | 'eula';

@Component({
  selector: 'core-ui-settings',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    TranslateModule,
    MatButton
  ],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  private readonly legalHelpLinkLabelByUrlKey: Record<LegalUrlKey, string> = {
    gdpr: 'SETTINGS.LEGAL.PRIVACY_POLICY',
    termsAndConditions: 'SETTINGS.LEGAL.TERMS_AND_CONDITIONS',
    eula: 'SETTINGS.LEGAL.EULA'
  };

  protected settingsForm: FormGroup;
  protected passwordChangeForm: FormGroup;
  protected disablePasswordChangeButton = false;
  protected legalHelpLinks: LegalLink[] = [];
  private settingsService = inject(UserSettingsService);
  private accountService = inject(AccountService);
  private appConfigService: AppConfigService = inject(AppConfigService);
  private snackBarService = inject(SnackBarService);

  public ngOnInit(): void {
    this.legalHelpLinks = this.getLegalLinksFromAppConfig(this.appConfigService.appConfig);
    this.settingsFormInit();
    this.passwordFormInit();
  }

  private passwordFormInit(): void {
    this.passwordChangeForm = new FormGroup(
      {
        password: new FormControl('', [Validators.required, CMSValidators.password]),
        passwordAgain: new FormControl('', Validators.required)
      },
      { validators: CMSValidators.matchingFields('password', 'passwordAgain') }
    );
  }

  private settingsFormInit(): void {
    const settings = this.settingsService.getUserSettings();
    this.settingsForm = new FormGroup({
      language: new FormControl(settings.language),
      timeFormat: new FormControl(settings.timeFormat),
      colorScheme: new FormControl(settings.colorScheme)
    });

    this.settingsForm.valueChanges
      .pipe(
        map(() => this.getFormChanges(this.settingsForm)),
        tap((changes) => {
          this.settingsService.saveUserSettings(changes);
          this.settingsForm.markAsPristine();
        })
      )
      .subscribe();
  }

  private getFormChanges(form: FormGroup): Partial<UserSettings> {
    const changedSettings = Object.entries(form.controls).reduce((changes, keyValuePair) => {
      if (keyValuePair[1].dirty) {
        const change: any = {};
        change[keyValuePair[0]] = keyValuePair[1].value;
        changes.push(change);
      }
      return changes;
    }, []);

    return Object.assign({}, ...changedSettings);
  }

  protected savePassword(): void {
    if (this.passwordChangeForm.invalid) {
      return;
    }
    const password = this.passwordChangeForm.get('password').value;
    this.disablePasswordChangeButton = true;

    this.accountService
      .changePassword$(password)
      .pipe(
        tap(() => this.snackBarService.popGenericSuccess()),
        catchError(() => {
          this.snackBarService.popGenericError();
          return of();
        }),
        finalize(() => {
          this.disablePasswordChangeButton = false;
          this.passwordChangeForm.reset();

          this.passwordChangeForm.get('password').clearValidators();
          this.passwordChangeForm.get('password').updateValueAndValidity();
          this.passwordChangeForm.get('passwordAgain').clearValidators();
          this.passwordChangeForm.get('passwordAgain').updateValueAndValidity();
        })
      )
      .subscribe();
  }

  protected get ColorScheme(): typeof ColorScheme {
    return ColorScheme;
  }

  protected get timeFormats(): string[] {
    return Object.values(TimeFormat);
  }

  protected get languages(): string[] {
    return providerConfig.languages;
  }

  protected get userCanModifyViewMode(): boolean {
    return this.appConfigService.appConfig?.features.userRoles;
  }

  protected get userCanChangePassword(): boolean {
    return this.appConfigService.appConfig?.features.userRoles;
  }

  private getLegalLinksFromAppConfig(appConfig: AppConfig): LegalLink[] {
    const urls = appConfig?.urls;
    if (!urls) {
      return [];
    }

    const links: LegalLink[] = [];
    for (const [urlKey, labelTranslationKey] of Object.entries(this.legalHelpLinkLabelByUrlKey)) {
      const url: string = urls[urlKey as keyof typeof urls];
      if (url.trim().length > 0) {
        links.push({ labelTranslationKey, url });
      }
    }
    return links;
  }
}
